import { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const PAGE_SIZE = 20;

/**
 * Client-side paginator hook. Recibe una lista `items` (ya filtrada por el
 * consumidor) y devuelve la página actual + controles Anterior/Siguiente.
 * Se resetea a la página 1 cada vez que cambia la longitud de la lista para
 * mantener coherencia con filtros/búsqueda del consumidor.
 *
 * Uso:
 *   const { pageItems, page, setPage, totalPages, total } = usePagination(items);
 *   ...
 *   <Pagination page={page} totalPages={totalPages} total={total}
 *               setPage={setPage} testIdPrefix="admin-orders" />
 */
export function usePagination(items, pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Reset a página 1 cuando el conjunto cambia (nuevo filtro/búsqueda o recarga).
  // Comparamos por longitud para no reinstanciar en cada render.
  useEffect(() => {
    setPage(1);
  }, [total]);

  // Clamp por si el consumidor pasó una página fuera de rango.
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = (safePage - 1) * pageSize;
  const pageItems = useMemo(() => items.slice(from, from + pageSize), [items, from, pageSize]);

  return { pageItems, page: safePage, setPage, totalPages, total, from, pageSize };
}

/**
 * Barra de paginación: contador + botones Anterior/Siguiente. Mostrar solo
 * cuando hay más de 1 página o el consumidor quiere exponer el contador
 * incluso con pocos items (`alwaysShow`).
 */
export function Pagination({ page, totalPages, total, setPage, testIdPrefix, alwaysShow = true, className = '' }) {
  if (!alwaysShow && totalPages <= 1) return null;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);
  return (
    <div className={`flex items-center justify-between gap-3 py-3 px-1 text-xs text-slate-600 ${className}`}>
      <div data-testid={testIdPrefix ? `${testIdPrefix}-counter` : 'pagination-counter'}>
        {total === 0 ? 'Sin resultados' : (
          <>
            <span className="font-semibold text-slate-900">{from}-{to}</span>
            <span className="text-slate-500"> de {total} registros</span>
            {totalPages > 1 && (
              <span className="text-slate-400"> · página {page}/{totalPages}</span>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          data-testid={testIdPrefix ? `${testIdPrefix}-prev` : 'pagination-prev'}
          variant="outline"
          size="sm"
          className="rounded-none h-8 px-3"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Anterior
        </Button>
        <Button
          data-testid={testIdPrefix ? `${testIdPrefix}-next` : 'pagination-next'}
          variant="outline"
          size="sm"
          className="rounded-none h-8 px-3"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Siguiente <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
